import { Box, Text, HStack, Badge } from '@chakra-ui/react';
import { InbreedingRiskLevel } from '@/types/ai';

interface InbreedingIndicatorProps {
  /** 近親係数（0-1） */
  coefficient: number;
  /** リスクレベル */
  riskLevel: InbreedingRiskLevel;
  /** コンパクト表示 */
  compact?: boolean;
  /** パーセント表示 */
  showPercent?: boolean;
}

/**
 * リスクレベルに対応する色を取得
 */
function getRiskColor(riskLevel: InbreedingRiskLevel): string {
  switch (riskLevel) {
    case 'safe':
      return 'green';
    case 'caution':
      return 'yellow';
    case 'warning':
      return 'orange';
    case 'danger':
      return 'red';
  }
}

/**
 * リスクレベルに対応するラベルを取得
 */
function getRiskLabel(riskLevel: InbreedingRiskLevel): string {
  switch (riskLevel) {
    case 'safe':
      return '安全';
    case 'caution':
      return '注意';
    case 'warning':
      return '警告';
    case 'danger':
      return '危険';
  }
}

/**
 * リスクレベルに対応する説明を取得
 */
function getRiskDescription(riskLevel: InbreedingRiskLevel): string {
  switch (riskLevel) {
    case 'safe':
      return '遺伝的多様性が十分に確保されています';
    case 'caution':
      return 'いとこ同士程度の近縁関係です';
    case 'warning':
      return '近親交配のリスクがあります';
    case 'danger':
      return '親子・兄弟レベルの近縁関係です';
  }
}

/**
 * 近親係数インジケータコンポーネント
 */
export function InbreedingIndicator({
  coefficient,
  riskLevel,
  compact = false,
  showPercent = true,
}: InbreedingIndicatorProps) {
  const color = getRiskColor(riskLevel);
  const label = getRiskLabel(riskLevel);
  const percent = (coefficient * 100).toFixed(2);

  if (compact) {
    return (
      <HStack gap={2}>
        <Badge colorPalette={color} size="sm">
          {label}
        </Badge>
        {showPercent && (
          <Text fontSize="xs" color="gray.600">
            {percent}%
          </Text>
        )}
      </HStack>
    );
  }

  return (
    <Box
      p={3}
      borderRadius="md"
      bg={`${color}.50`}
      borderLeft="4px solid"
      borderLeftColor={`${color}.500`}
    >
      <HStack justify="space-between" mb={1}>
        <HStack gap={2}>
          <Text fontSize="sm" fontWeight="medium" color={`${color}.700`}>
            近親係数
          </Text>
          <Badge colorPalette={color} size="sm">
            {label}
          </Badge>
        </HStack>
        <Text fontSize="sm" fontWeight="bold" color={`${color}.700`}>
          {percent}%
        </Text>
      </HStack>
      <Text fontSize="xs" color={`${color}.600`}>
        {getRiskDescription(riskLevel)}
      </Text>
    </Box>
  );
}

/**
 * 近親係数バッジコンポーネント（簡易表示用）
 */
export function InbreedingBadge({
  riskLevel,
}: {
  riskLevel: InbreedingRiskLevel;
}) {
  const color = getRiskColor(riskLevel);
  const label = getRiskLabel(riskLevel);

  return (
    <Badge colorPalette={color} size="sm">
      {label}
    </Badge>
  );
}
